import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import {
    CalendarIcon,
    ClockIcon,
    EyeIcon,
    HeartIcon,
    ShareIcon,
    TagIcon,
    UserIcon,
    PhotoIcon,
    ArrowLeftIcon
} from '@heroicons/react/24/outline'
import { blogAPI } from '../../services/api'
import LoadingSpinner from '../../components/Common/LoadingSpinner'
import ErrorMessage from '../../components/Common/ErrorMessage'

const BlogDetail = () => {
    const { slug } = useParams()

    const { data, isLoading, error } = useQuery({
        queryKey: ['blog', slug],
        queryFn: () => blogAPI.getBlog(slug),
    })

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
    }


    const handleShare = async () => {
        try {
            await blogAPI.shareBlog(data.data.blog._id)

            // Try to use native share API if available
            if (navigator.share) {
                await navigator.share({
                    title: data.data.blog.title,
                    text: data.data.blog.excerpt,
                    url: window.location.href,
                })
            } else {
                // Fallback: copy to clipboard
                await navigator.clipboard.writeText(window.location.href)
                alert('Link copied to clipboard!')
            }
        } catch (error) {
            console.error('Failed to share blog:', error)
        }
    }

    if (isLoading) return <LoadingSpinner />
    if (error) return <ErrorMessage message="Failed to load blog" />

    const blog = data?.data?.blog
    const relatedBlogs = data?.data?.related_blogs || []

    if (!blog) {
        return <ErrorMessage message="Blog not found" />
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Helmet>
                <title>{blog.title} | Margdarshak Blog</title>
                <meta name="description" content={blog.excerpt} />
                <meta name="keywords" content={blog.meta_keywords?.join(', ') || blog.tags?.join(', ')} />
                <meta property="og:title" content={blog.title} />
                <meta property="og:description" content={blog.excerpt} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={window.location.href} />
                {blog.featured_image && (
                    <meta property="og:image" content={blog.featured_image} />
                )}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={blog.title} />
                <meta name="twitter:description" content={blog.excerpt} />
                {blog.featured_image && (
                    <meta name="twitter:image" content={blog.featured_image} />
                )}
            </Helmet>

            {/* Back Button */}
            <div className="bg-white border-b border-gray-200 pt-14 mt-16">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <Link
                        to="/blogs"
                        className="inline-flex items-center text-primary-600 hover:text-primary-700 transition-colors"
                    >
                        <ArrowLeftIcon className="h-5 w-5 mr-2" />
                        Back to Blogs
                    </Link>
                </div>
            </div>

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <motion.article
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                >
                    {/* Featured Image */}
                    {blog.featured_image && (
                        <div className="aspect-video bg-gray-200">
                            <img
                                src={blog.featured_image}
                                alt={blog.title}
                                className="w-full h-full object-cover"
                            />
                        </div>
                    )}

                    <div className="p-8">
                        {/* Category & Featured Badge */}
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center space-x-3">
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                    {blog.category}
                                </span>
                                {blog.featured && (
                                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                                        Featured
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Title */}
                        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight">
                            {blog.title}
                        </h1>

                        {/* Meta Information */}
                        <div className="flex flex-wrap items-center gap-6 mb-8 pb-6 border-b border-gray-200">
                            <div className="flex items-center text-gray-600">
                                <UserIcon className="h-5 w-5 mr-2" />
                                <span className="font-medium">{blog.created_by?.name || 'Admin'}</span>
                            </div>
                            <div className="flex items-center text-gray-600">
                                <CalendarIcon className="h-5 w-5 mr-2" />
                                <span>{formatDate(blog.published_at || blog.createdAt)}</span>
                            </div>
                            <div className="flex items-center text-gray-600">
                                <ClockIcon className="h-5 w-5 mr-2" />
                                <span>{blog.reading_time} min read</span>
                            </div>
                            <div className="flex items-center text-gray-600">
                                <EyeIcon className="h-5 w-5 mr-2" />
                                <span>{blog.views || 0} views</span>
                            </div>
                        </div>

                        {/* Tags */}
                        {blog.tags && blog.tags.length > 0 && (
                            <div className="mb-8">
                                <h3 className="text-sm font-medium text-gray-700 mb-3">Tags</h3>
                                <div className="flex flex-wrap gap-2">
                                    {blog.tags.map((tag, index) => (
                                        <span
                                            key={index}
                                            className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700"
                                        >
                                            <TagIcon className="h-4 w-4 mr-1" />
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Content */}
                        <div className="prose prose-lg max-w-none mb-8">
                            <div
                                dangerouslySetInnerHTML={{ __html: blog.content }}
                                className="blog-content"
                            />
                        </div>

                        {/* Gallery Images */}
                        {blog.gallery_images && blog.gallery_images.length > 0 && (
                            <div className="mb-8">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Gallery</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {blog.gallery_images.map((image, index) => (
                                        <div key={index} className="aspect-video bg-gray-200 rounded-lg overflow-hidden">
                                            <img
                                                src={image}
                                                alt={`${blog.title} - Image ${index + 1}`}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                            <div className="flex items-center space-x-4">
                                <button
                                    onClick={handleShare}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                                >
                                    <ShareIcon className="h-5 w-5 mr-2" />
                                    Share ({blog.shares || 0})
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.article>

                {/* Related Blogs */}
                {relatedBlogs.length > 0 && (
                    <div className="mt-12">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Blogs</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {relatedBlogs.map((relatedBlog, index) => (
                                <motion.article
                                    key={relatedBlog._id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                                >
                                    {/* Featured Image */}
                                    <div className="aspect-video bg-gray-200">
                                        {relatedBlog.featured_image ? (
                                            <img
                                                src={relatedBlog.featured_image}
                                                alt={relatedBlog.title}
                                                className="w-full h-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <PhotoIcon className="h-12 w-12 text-gray-400" />
                                            </div>
                                        )}
                                    </div>

                                    <div className="p-6">
                                        {/* Category */}
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mb-3">
                                            {relatedBlog.category}
                                        </span>

                                        {/* Title */}
                                        <h3 className="text-lg font-semibold text-gray-900 mb-3 line-clamp-2">
                                            <Link
                                                to={`/blogs/${relatedBlog.slug}`}
                                                className="hover:text-primary-600 transition-colors"
                                            >
                                                {relatedBlog.title}
                                            </Link>
                                        </h3>

                                        {/* Excerpt */}
                                        <p className="text-gray-600 mb-4 line-clamp-3">
                                            {relatedBlog.excerpt}
                                        </p>

                                        {/* Meta Information */}
                                        <div className="flex items-center justify-between text-sm text-gray-500">
                                            <div className="flex items-center">
                                                <CalendarIcon className="h-4 w-4 mr-1" />
                                                {formatDate(relatedBlog.published_at || relatedBlog.createdAt)}
                                            </div>
                                            <div className="flex items-center space-x-3">
                                                <div className="flex items-center">
                                                    <EyeIcon className="h-4 w-4 mr-1" />
                                                    {relatedBlog.views || 0}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </motion.article>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Custom CSS for blog content */}
            <style jsx>{`
                .blog-content h1,
                .blog-content h2,
                .blog-content h3,
                .blog-content h4,
                .blog-content h5,
                .blog-content h6 {
                    margin-top: 2rem;
                    margin-bottom: 1rem;
                    font-weight: 600;
                    line-height: 1.25;
                }

                .blog-content h1 {
                    font-size: 2.25rem;
                }

                .blog-content h2 {
                    font-size: 1.875rem;
                }

                .blog-content h3 {
                    font-size: 1.5rem;
                }

                .blog-content p {
                    margin-bottom: 1.5rem;
                    line-height: 1.75;
                }

                .blog-content ul,
                .blog-content ol {
                    margin-bottom: 1.5rem;
                    padding-left: 1.5rem;
                }

                .blog-content li {
                    margin-bottom: 0.5rem;
                }

                .blog-content blockquote {
                    border-left: 4px solid #3b82f6;
                    padding-left: 1rem;
                    margin: 1.5rem 0;
                    font-style: italic;
                    color: #6b7280;
                }

                .blog-content img {
                    max-width: 100%;
                    height: auto;
                    border-radius: 0.5rem;
                    margin: 1.5rem 0;
                }

                .blog-content a {
                    color: #3b82f6;
                    text-decoration: underline;
                }

                .blog-content a:hover {
                    color: #1d4ed8;
                }

                .blog-content code {
                    background-color: #f3f4f6;
                    padding: 0.25rem 0.5rem;
                    border-radius: 0.25rem;
                    font-size: 0.875rem;
                }

                .blog-content pre {
                    background-color: #1f2937;
                    color: #f9fafb;
                    padding: 1rem;
                    border-radius: 0.5rem;
                    overflow-x: auto;
                    margin: 1.5rem 0;
                }

                .blog-content pre code {
                    background-color: transparent;
                    padding: 0;
                    color: inherit;
                }
            `}</style>
        </div>
    )
}

export default BlogDetail
